import type { ClientDetail, UpdateClientInput } from "@closer/shared";
import {
  CLIENT_CLOSER_MODE_NAMES,
  CLIENT_LINK_STATE_NAMES,
  CLIENT_STATUS_NAMES,
  CLIENT_TYPE_FIELDS,
  CLIENT_TYPE_NAMES,
  closerModeOf,
  updateClientSchema,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  DefinitionGrid,
  FormField,
  Input,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { ActivityFeed } from "../../components/ActivityFeed";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { ClientContacts } from "./ClientContacts";
import { ClientLifecycleDialog } from "./ClientLifecycleDialog";
import { ClientLinkInviteDialog } from "./ClientLinkInviteDialog";
import { ClientMergeDialog } from "./ClientMergeDialog";
import { ClientPortfolio } from "./ClientPortfolio";
import { ClientPortfolioAssignments } from "./ClientPortfolioAssignments";
import { ClientRepresentations } from "./ClientRepresentations";
import { ClientRepresentativeInvitations } from "./ClientRepresentativeInvitations";
import { ClientServiceRelationships } from "./ClientServiceRelationships";
import { ClientWorkspace, useClientWorkspace } from "./ClientWorkspace";

/**
 * MÜVEKKİL DETAYI ve DÜZENLEME (CLOS-170 `[117]`).
 *
 * ### ⚠ Bağlı bölümler KENDİ erişimleriyle
 *
 * Kart: *"bağlı sözleşme, temsilci, hizmet ilişkisi ve portföy bölümleri KENDİ KAYIT
 * ERİŞİMİYLE ayrı filtrelenir; detay ekranı bunları OTOMATİK AÇMAZ."* Portföy bölümü
 * `[120]` ile doğdu ve sunucuda kullanıcının erişebildiği sözleşmelerle süzülüyor: müvekkili
 * görmek, portföyündeki her sözleşmeyi görmek değil. Temsilci daveti bölümü (`[124]`) aynı
 * kurala uyuyor: `client.representative` yoksa bölüm hiç çizilmiyor ve davet listesi
 * sunucuya HİÇ sorulmuyor — gizlemek değil, istememek.
 *
 * ### ⚠ Düzenleme yetkisi ayrı
 *
 * Form yalnız `client.edit` varsa çiziliyor. Menüyü gizlemek bir güvenlik sınırı değil —
 * gerçek kapı uçtaki `requirePermission` — ama kullanıcıyı sunucudan 403 alacağı bir
 * forma oturtmak, emeğini boşa harcatmak olurdu.
 *
 * ### ⚠ Sürüm formun İÇİNDE
 *
 * Kullanıcının OKUDUĞU sürüm gizli alanda gidiyor. Sunucu "en son hâli" varsayarak
 * yazsaydı, aynı müvekkili aynı anda düzenleyen ikinci kişi ilkinin değişikliğini sessizce
 * ezerdi. 409 geldiğinde ekran kaydı yeniden okuyor ve kullanıcıya güncel hâli gösteriyor
 * (apps/web/CLAUDE.md: *"CONCURRENCY_CONFLICT → kaydı yeniden oku"*).
 */

const ARSIV = "archived";
const BIRLESIK = "merged";

export function ClientDetailPage() {
  const { activeTenantId, can } = useAuth();
  /**
   * ⚠ Alanın varlığı burada bir kez okunuyor ve İKİ bölüme besleniyor (CLOS-865): kart
   * durumu gösteriyor, davet bölümü ona göre kilitleniyor. `undefined` hâli bilerek
   * korunuyor — bilgi gelmeden daveti kilitlemek, alanı olan müvekkilde yanlış olurdu.
   */
  const alanSorgusu = useClientWorkspace(useParams<{ id: string }>().id ?? "");
  const alanKurulu = alanSorgusu.sorgu.isPending
    ? undefined
    : alanSorgusu.sorgu.data !== null && alanSorgusu.sorgu.data !== undefined;
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [birlestirmeAcik, setBirlestirmeAcik] = useState(false);
  const [davetAcik, setDavetAcik] = useState(false);
  /**
   * ⚠ Modalin "Şimdi davet et" adımı buraya `?davet=1` ile geliyor (CLOS-1116): kayıt
   * açıldıktan sonra davet penceresini kullanıcının ikinci kez aramasına gerek kalmıyor.
   * Parametre okununca TEMİZLENİYOR — yenilemede pencere kendiliğinden yeniden açılırsa
   * kullanıcı kapattığı bir şeyle tekrar karşılaşır.
   */
  const [aramaParams, setAramaParams] = useSearchParams();
  const [yasamDongusu, setYasamDongusu] = useState<"archive" | "reactivate" | null>(null);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "client", id),
    queryFn: async () => (await api<ClientDetail>(`/api/clients/${id}`, { tenantId })).data,
  });

  const form = useZodForm(updateClientSchema);
  const musvekkil = sorgu.data;

  useEffect(() => {
    if (aramaParams.get("davet") !== "1") return;
    setDavetAcik(true);
    const kalan = new URLSearchParams(aramaParams);
    kalan.delete("davet");
    setAramaParams(kalan, { replace: true });
  }, [aramaParams, setAramaParams]);

  useEffect(() => {
    if (!musvekkil) return;
    form.reset({
      version: musvekkil.version,
      firstName: musvekkil.firstName ?? undefined,
      lastName: musvekkil.lastName ?? undefined,
      nationalId: musvekkil.nationalId ?? undefined,
      legalName: musvekkil.legalName ?? undefined,
      taxNumber: musvekkil.taxNumber ?? undefined,
      taxOffice: musvekkil.taxOffice ?? undefined,
      email: musvekkil.email ?? undefined,
      phone: musvekkil.phone ?? undefined,
    });
  }, [musvekkil, form]);

  const kaydet = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ClientDetail>(`/api/clients/${id}`, {
        method: "PATCH",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "client", id) });
      toast.success("Müvekkil güncellendi");
    },
    onError: async (hata) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      /**
       * ⚠ Çakışmada kayıt YENİDEN OKUNUYOR: kullanıcı elindeki eski hâli tekrar
       * göndermeye çalışmasın, başkasının yazdığını görsün. Körlemesine tekrar denemek
       * başkasının değişikliğini ezmek olurdu.
       */
      if (hata instanceof ApiRequestError && hata.code === "CONCURRENCY_CONFLICT") {
        await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "client", id) });
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Müvekkil güncellenemedi");
    },
  });

  const birlestir = useMutation({
    mutationFn: (girdi: { sourceId: string; sourceVersion: number; targetVersion: number }) =>
      api<{ targetId: string }>(`/api/clients/${id}/merge`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      setBirlestirmeAcik(false);
      /**
       * ⚠ Hem detay hem LİSTE tazeleniyor: kapanan kayıt listeden düşmeli. Yalnız detayı
       * tazeleseydik kullanıcı geri döndüğünde artık var olmayan bir kaydı görürdü.
       *
       * ⚠ PORTFÖY de tazeleniyor (kusur avı, 26 Ağustos): birleştirme kapanan kaydın
       * sözleşme ilişkilerini hedefe TAŞIYOR. Tazelemeyince hemen yandaki bölüm eski
       * listeyi gösteriyor ve kullanıcı taşınan sözleşmeleri göremiyordu.
       */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "client", id) });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "clients") });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "client-portfolio", id),
      });
      toast.success("Mükerrer kayıt birleştirildi");
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "Kayıtlar birleştirilemedi");
    },
  });

  const durumDegistir = useMutation({
    mutationFn: (girdi: {
      action: "archive" | "reactivate";
      version: number;
      acknowledge: boolean;
    }) =>
      api<{ status: string }>(`/api/clients/${id}/lifecycle/${girdi.action}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ version: girdi.version, acknowledge: girdi.acknowledge }),
      }),
    onSuccess: async (_veri, girdi) => {
      setYasamDongusu(null);
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "client", id) });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "clients") });
      toast.success(
        girdi.action === "archive" ? "Müvekkil arşivlendi" : "Müvekkil yeniden aktifleştirildi",
      );
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "Durum değiştirilemedi");
    },
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Müvekkil yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (sorgu.error || !musvekkil) {
    return (
      <StateView
        state={resolveErrorState(sorgu.error, { idempotent: true })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }

  /**
   * ⚠ Mezar taşı 404 DEĞİL, YÖNLENDİRME. Kart: *"redirect/tombstone ve eski kimlik
   * izlenebilirliği korunur."* Eski bağlantıyla gelen kullanıcı "kayıt yok" değil "bu kayıt
   * şuraya taşındı" cevabını alıyor; otomatik atlamıyoruz çünkü kullanıcı NEDEN başka bir
   * kayda baktığını bilmeli.
   */
  if (musvekkil.status === BIRLESIK && musvekkil.mergedIntoId) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">{musvekkil.displayName}</h1>
        <Card>
          <p className="text-sm text-fg">
            Bu kayıt mükerrer olduğu için kapatıldı ve başka bir müvekkil kaydına birleştirildi.
          </p>
          <Link
            className="text-sm font-medium text-primary hover:underline"
            to={`/app/muvekkiller/${musvekkil.mergedIntoId}`}
          >
            Güncel kayda git
          </Link>
        </Card>
      </div>
    );
  }

  const arsivde = musvekkil.status === ARSIV;
  const alanlar = new Set(CLIENT_TYPE_FIELDS[musvekkil.clientType]);

  /**
   * ⚠ YALNIZ DEĞİŞEN alanlar gönderiliyor. Dokunulmayan alanı yeniden yazmak, geçmişte
   * "kim neyi değiştirdi" sorusunu bulanıklaştırır ve her kaydetme bütün alanları
   * değişmiş gösterirdi.
   */
  const gonder = form.handleSubmit((girdi: UpdateClientInput) => {
    const govde: Record<string, unknown> = { version: musvekkil.version };
    const mevcut = musvekkil as unknown as Record<string, unknown>;
    for (const [alan, deger] of Object.entries(girdi)) {
      if (alan === "version" || !alanlar.has(alan)) continue;
      const yeni = deger === "" || deger === undefined ? null : deger;
      if (yeni !== (mevcut[alan] ?? null)) govde[alan] = yeni;
    }
    if (Object.keys(govde).length === 1) {
      toast.error("Değişen bir alan yok");
      return;
    }
    kaydet.mutate(govde);
  });

  return (
    <div className="flex w-full flex-col gap-5">
      <header className="flex flex-wrap items-start gap-3">
        <div className="mr-auto flex min-w-0 flex-col gap-2">
          {/**
           * ⚠ Kırıntı yolu DEĞİL, tek bir geri bağlantısı — gerekçe `ContractDetailLayout`
           * ile aynı: tasarımdaki ikinci kırıntı öğesi başlığın kendisi ve o metin hemen
           * altında zaten duruyor. Aynı metni iki düğüme yaymak, kayıt adını arayan
           * bekçileri ikircikli bırakıyor.
           */}
          <Link
            to="/app/muvekkiller"
            className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-soft transition-colors hover:text-primary"
          >
            <span aria-hidden="true">←</span> Müvekkiller
          </Link>
          <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">
            {musvekkil.displayName}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{CLIENT_TYPE_NAMES[musvekkil.clientType]}</Badge>
            {arsivde && <Badge tone="neutral">{CLIENT_STATUS_NAMES.archived}</Badge>}
            {/**
             * ⚠ CLOSER BAĞI ROZETİ (CLOS-1107). Denetimin bulgusu: yerel kayıt ile Closer
             * organizasyonu arasındaki bağ hiçbir yerde GÖRÜNMÜYORDU — kullanıcı kaydın
             * "sadece bizde" mi durduğunu yoksa karşı tarafla eşleşmiş mi olduğunu
             * ekrandan okuyamıyordu.
             *
             * ⚠ ROZET ERİŞİM ANLATMIYOR: "Closer'a bağlı", karşı tarafın kayıtlarımızı
             * gördüğü anlamına gelmez; erişim her nesnede ayrıca verilir.
             */}
            {/* Kullanım biçimi (CLOS-1122): Temsili / Closer — bağdan TÜRETİLİR, kolon değil. */}
            <Badge tone="neutral">
              {CLIENT_CLOSER_MODE_NAMES[closerModeOf(musvekkil.closerLink.state)]}
            </Badge>
            <Badge tone={musvekkil.closerLink.state === "linked" ? "success" : "neutral"}>
              {CLIENT_LINK_STATE_NAMES[musvekkil.closerLink.state]}
            </Badge>
            {musvekkil.closerLink.linkedTenantName ? (
              <span className="text-xs text-fg-soft">{musvekkil.closerLink.linkedTenantName}</span>
            ) : null}
          </div>
        </div>
        {/*
          ⚠ Birleştirme AYRI YETKİDE (`client.merge`): düzenlemek bir alanı değiştirmek,
          birleştirmek iki kaydı geri alınamaz biçimde tek kayda indirmek.
        */}
        {/**
         * ⚠ DAVET YALNIZ BAĞSIZ VE AKTİF KAYITTA: bağlıysa yapacak bir şey yok, bekleyen
         * davet varsa ikinci davet sunucuda zaten 409 alır ve kullanıcıya reddedilecek bir
         * düğme göstermek olurdu. Kapı `client.service_scope` — hizmet ilişkisi ucunun
         * kapısıyla birebir aynı.
         */}
        {!arsivde && can("client.service_scope") && musvekkil.closerLink.state === "not_linked" && (
          <Button type="button" variant="secondary" onClick={() => setDavetAcik(true)}>
            Closer'a davet et
          </Button>
        )}
        {!arsivde && can("client.merge") && (
          <Button type="button" variant="secondary" onClick={() => setBirlestirmeAcik(true)}>
            Mükerrer kaydı birleştir
          </Button>
        )}
        {/*
          ⚠ Arşivleme AYRI YETKİDE (`client.archive`, `[119]`): telefon numarası düzeltmesi
          için yetki verilen herkes bir müvekkili gündemden çıkarabilmemeli.
        */}
        {can("client.archive") && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setYasamDongusu(arsivde ? "reactivate" : "archive")}
          >
            {arsivde ? "Yeniden aktifleştir" : "Arşivle"}
          </Button>
        )}
      </header>

      {yasamDongusu && (
        <ClientLifecycleDialog
          clientId={musvekkil.id}
          action={yasamDongusu}
          open
          onClose={() => setYasamDongusu(null)}
          onConfirm={(girdi) => durumDegistir.mutate({ ...girdi, action: yasamDongusu })}
          isPending={durumDegistir.isPending}
        />
      )}

      <ClientMergeDialog
        targetId={musvekkil.id}
        targetName={musvekkil.displayName}
        open={birlestirmeAcik}
        onClose={() => setBirlestirmeAcik(false)}
        onConfirm={(girdi) => birlestir.mutate(girdi)}
        isPending={birlestir.isPending}
      />

      <ClientLinkInviteDialog
        open={davetAcik}
        clientId={musvekkil.id}
        clientType={musvekkil.clientType}
        varsayilanEposta={musvekkil.email}
        onClose={() => setDavetAcik(false)}
      />

      <Card>
        <DefinitionGrid
          items={[
            { key: "acan", label: "Kaydı açan", value: musvekkil.createdByName },
            {
              key: "degisiklik",
              label: "Son değişiklik",
              value: new Date(musvekkil.updatedAt).toLocaleDateString("tr-TR"),
            },
          ]}
        />
      </Card>

      {/*
        ⚠ Portföy bölümü ARŞİVDE de görünüyor: kart *"sözleşme veya müvekkil arşivlendiğinde
        ilişki TARİHSEL GÖRÜNÜMDE korunur"* diyor. Yönetim düğmeleri ise arşivde kapalı —
        gündemden çıkmış bir kaydın portföyünü düzenlemek, arşivi anlamsız kılardı.
      */}
      {/*
        ⚠ YETKİ `client.contract_link`, `client.portfolio` DEĞİL (CLOS-823).
        CLOS-819 sunucu tarafında sözleşme bağını büro içi portföy sorumluluğundan ayırdı;
        burası eski anahtarı sormaya devam ediyordu. Sonuç: bağımsız avukat için uç açık
        ama yüzey kapalıydı — "kodda var, panelde yok" kusurunun tam örneği.
      */}
      <ClientPortfolio
        clientId={musvekkil.id}
        canManage={!arsivde && can("client.contract_link")}
      />

      {/*
        ⚠ İletişim kişileri (`[121]`) MÜVEKKİLİN BİLGİSİ: yönetimi `client.edit` yetkisine
        bağlı. Ayrı bir anahtar açmak, "müvekkili düzenleyebilen ama telefonunu
        düzenleyemeyen" gibi kimsenin istemediği bir rol üretirdi.
      */}
      <ClientContacts clientId={musvekkil.id} canManage={!arsivde && can("client.edit")} />

      {/*
        ⚠ Temsilci daveti (`[124]`) iletişim kişilerinden SONRA: davet formundaki aday
        listesi oradaki işaretten besleniyor, sıra kullanıcının kurduğu zincirle aynı.
      */}
      {/*
        ⚠ Müvekkil Alanı davetten ÖNCE (CLOS-865): davet alanın varlığına bağlı ve sunucu
        alan yokken 409 veriyor. Sıra tersine olsaydı kullanıcı, koşulunu henüz görmediği
        bir eylemi denerdi.
      */}
      <ClientWorkspace
        clientId={musvekkil.id}
        arsivde={arsivde}
        clientType={musvekkil.clientType}
      />

      {/*
        ⚠ GERÇEK KİŞİ MÜVEKKİLDE TEMSİLCİ KATMANI YOK (CLOS-1122 R1.2 / Halit S5): kişi
        alana kendisi olarak girer. Bölümler çizilmez; sunucu da bu müvekkil için temsil
        projeksiyonunu boş döndürür — gizleme yalnız ekranda değil.
      */}
      {musvekkil.clientType === "organization" ? (
        <ClientRepresentativeInvitations
          clientId={musvekkil.id}
          arsivde={arsivde}
          alanKurulu={alanKurulu}
        />
      ) : null}

      {/*
        ⚠ Temsil ilişkisi davetin HEMEN ALTINDA: kabul edilmiş davet burada temsile
        dönüşüyor. Sıra tersine olsaydı, kullanıcı kaynağını görmediği bir listeye bakardı.
      */}
      {musvekkil.clientType === "organization" ? (
        <ClientRepresentations clientId={musvekkil.id} />
      ) : null}

      {/*
        ⚠ Hizmet ilişkisi temsilden SONRA: müvekkil tarafındaki kararı çoğu zaman
        `service_actions` kapsamlı temsilci veriyor; zincir ekranda da bu sırayla okunuyor.
      */}
      <ClientServiceRelationships clientId={musvekkil.id} arsivde={arsivde} />

      {/*
        ⚠ Portföy sorumluluğu hizmet ilişkisinden SONRA: sorumluluk AKTİF bir ilişkiye
        tanımlanıyor, ekranda da o sıra okunuyor.
      */}
      <ClientPortfolioAssignments clientId={musvekkil.id} />

      {arsivde ? (
        <Card>
          {/* Arşivdeki kayıt OKUNUYOR ama düzenlenmiyor — sunucunun kuralı, ekran onu söylüyor. */}
          <p className="text-sm text-fg">
            Bu müvekkil arşivde. Bilgilerini düzenlemek için önce yeniden aktifleştirilmesi
            gerekiyor.
          </p>
        </Card>
      ) : can("client.edit") ? (
        <Card>
          <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
            {/* ⚠ Sürüm gizli: kullanıcının OKUDUĞU hâl ile yazdığı hâl aynı olmalı. */}
            <Input type="hidden" {...form.register("version")} />

            {musvekkil.clientType === "individual" ? (
              <>
                <FormField label="Ad" error={form.formState.errors.firstName?.message} required>
                  <Input {...form.register("firstName")} />
                </FormField>
                <FormField label="Soyad" error={form.formState.errors.lastName?.message} required>
                  <Input {...form.register("lastName")} />
                </FormField>
                <FormField
                  label="Kimlik numarası"
                  error={form.formState.errors.nationalId?.message}
                >
                  <Input {...form.register("nationalId")} />
                </FormField>
              </>
            ) : (
              <>
                <FormField
                  label="Ticari unvan"
                  error={form.formState.errors.legalName?.message}
                  required
                >
                  <Input {...form.register("legalName")} />
                </FormField>
                <FormField label="Vergi numarası" error={form.formState.errors.taxNumber?.message}>
                  <Input {...form.register("taxNumber")} />
                </FormField>
                <FormField label="Vergi dairesi" error={form.formState.errors.taxOffice?.message}>
                  <Input {...form.register("taxOffice")} />
                </FormField>
              </>
            )}

            <FormField label="E-posta" error={form.formState.errors.email?.message}>
              <Input type="email" {...form.register("email")} />
            </FormField>
            <FormField label="Telefon" error={form.formState.errors.phone?.message}>
              <Input {...form.register("phone")} />
            </FormField>

            <div className="flex justify-end">
              <Button type="submit" loading={kaydet.isPending}>
                Değişiklikleri kaydet
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card>
          <p className="text-sm text-fg-soft">Bu müvekkilin bilgilerini düzenleme yetkiniz yok.</p>
        </Card>
      )}

      {/*
        Aktivite geçmişi (`[147]` CLOS-285) — MÜVEKKİLİN BAĞLAMINDA, ayrı bir sayfa değil.
        İletişim kişisi, sponsorlu alan, temsil ilişkisi, portföy sorumluluğu ve müvekkil
        kaydının kendi satırları burada tek akışta buluşuyor; süzme sunucuda (`client.view`
        yetkisi olmayan satır hiç inmiyor).

        ⚠ Arşivde de çiziliyor: portföy bölümüyle aynı gerekçe — kaydın NEDEN arşivlendiği
        ancak geçmişinden okunur.
      */}
      <ActivityFeed entityType="client" entityId={musvekkil.id} />
    </div>
  );
}
